import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { type RouteComponentProps, withRouter } from "react-router-dom";
import { type Dispatch, type UnknownAction } from "redux";
import { Mails } from "lucide-react";
import { useTranslation } from "react-i18next";

import { valueOrDefault, isEmpty, arrayToString } from "../../../../utils";
import { adaptUnitsOfMeasure } from "../../../../utils/adapters";
import { PageBreadcrumb } from "../../../../components/PageBreadcrumb";
import { Form } from ".";
import {
  addInfoHubSubjectDetail,
  infoHubDeleteAggregatorFromSubject,
  infoHubSubjectDetail,
  infoHubSubjectDetailReset,
  listAggregators,
  listAggregatorsReset,
  listInfoHubSubjectAggregators,
  listInfoHubSubjectAggregatorsReset,
  listInfoHubTopics,
  listInfoHubTopicsReset,
  listOrgTypes,
  listUnitsOfMeasure,
  listUnitsOfMeasureReset,
  updateInfoHubSubjectDetail,
} from "../../../../actions";
import { MCF_COOPERATING_PARTNER_ORG_TYPE } from "../../../../constants";
import { getUserLanguage } from "../../../../utils/i18n";
import type {
  Aggregator,
  AggregatorOption,
  InfoHubFieldLoading,
  InfoHubFormValues,
  InfoHubSubjectData,
  InfoHubSubjectPayload,
  OrgType,
  SaveSubjectResult,
  SelectOption,
  Topic,
} from "./types";

type OptionValue = number | string;

// Raw unit of measurement as returned by /api/units_of_measurement/.
interface RawUnitOfMeasurement {
  id: number | string;
  name: string;
  type: string;
}

// Reads the `.value` of a select-option field, or null when unset.
const optionValueOrNull = (option?: SelectOption | null): OptionValue | null =>
  option && option.value != null ? option.value : null;

// A function to adapt values entered in form to a format accepted by the server
export const adaptFromValues = (formValues: InfoHubFormValues): InfoHubSubjectPayload => {
  const topics = formValues.topics ?? [];
  const aggregators = formValues.aggregators ?? [];
  return {
    ...formValues,
    target: (topics.length > 0 && topics[0].target) || "AGGREGATOR",
    content_type: (formValues.content_type && String(formValues.content_type.value)) || "",
    topics: topics.map(topic => topic.value),
    aggregators: aggregators.map(aggregator => aggregator.value),
    language: getUserLanguage(),
    // Distribution fields: convert option objects to scalars; the backend nulls
    // out whichever group does not match the selected content type.
    input_distribution_category: optionValueOrNull(formValues.input_distribution_category),
    input_distribution_details: formValues.input_distribution_details || null,
    input_distribution_quantity: formValues.input_distribution_quantity ?? null,
    input_distribution_unit_of_measure: optionValueOrNull(
      formValues.input_distribution_unit_of_measure
    ),
    equipment_distribution_type: optionValueOrNull(formValues.equipment_distribution_type),
    equipment_distribution_details: formValues.equipment_distribution_details || null,
    equipment_distribution_quantity: formValues.equipment_distribution_quantity ?? null,
  };
};

// A function to adapt values received from server to
// a format compatible with the form
export const adaptInitialFormValues = (
  data: InfoHubSubjectData,
  selectedAggregators: OptionValue[],
  allAggregators: Aggregator[],
  units: SelectOption[]
): InfoHubFormValues => {
  if (isEmpty(data)) {
    return {};
  }
  // For category/type the label is resolved from the field options by value, so
  // a placeholder label is fine; the unit label is resolved from the units list.
  const codeOption = (code?: string | null): SelectOption | undefined =>
    code ? { value: code, label: code } : undefined;
  const unitOption = (unitId?: number | string | null): SelectOption | undefined => {
    if (unitId == null) return undefined;
    const match = units.find(unit => String(unit.value) === String(unitId));
    return { value: unitId, label: match ? match.label : String(unitId) };
  };

  return {
    ...data,
    target: (data.topics && data.topics.length > 0 && data.topics[0].target) || "AGGREGATOR",
    topics: valueOrDefault(
      data.topics &&
        data.topics.map(topic => ({
          value: topic.id,
          label: topic.name,
          target: topic.target,
        })),
      undefined
    ),
    content_type: valueOrDefault(
      data.content_type && { value: data.content_type, label: data.content_type },
      undefined
    ),
    aggregators: valueOrDefault(
      Array.isArray(selectedAggregators) &&
        selectedAggregators
          .map(aggregatorID => {
            const aggregator = allAggregators.find(item => item.id === aggregatorID);
            if (!aggregator) return undefined;
            return { value: aggregatorID, label: aggregator.name };
          })
          // Drop ids with no matching aggregator (would otherwise be undefined holes).
          .filter((option): option is AggregatorOption => option !== undefined),
      undefined
    ),
    input_distribution_category: codeOption(data.input_distribution_category),
    input_distribution_details: data.input_distribution_details ?? undefined,
    input_distribution_quantity: data.input_distribution_quantity ?? undefined,
    input_distribution_unit_of_measure: unitOption(data.input_distribution_unit_of_measure),
    equipment_distribution_type: codeOption(data.equipment_distribution_type),
    equipment_distribution_details: data.equipment_distribution_details ?? undefined,
    equipment_distribution_quantity: data.equipment_distribution_quantity ?? undefined,
  };
};

// A function to get the error message from the upload farmer
// response if there is a payload in the response
export const getResponseErrorMessage = (
  data: Record<string, unknown> | undefined
): string | undefined => {
  if (data && Object.keys(data).length > 0) {
    let errors = "";
    Object.keys(data).forEach(key => {
      errors += `${key}: ${arrayToString(data[key])}\n`;
    });
    return errors;
  }
};

// Creating from a dashboard "+ New" button pre-selects the chosen content type,
// using the same option shape the edit flow uses (see adaptInitialFormValues).
export const buildPresetInitialValues = (
  presetContentType?: string
): InfoHubFormValues | undefined =>
  presetContentType
    ? { content_type: { value: presetContentType, label: presetContentType } }
    : undefined;

// Editing/copying (id present) always uses the reconstructed subject values, so
// the preset is ignored there; a new message uses the preset when one was passed.
export const selectFormInitialValues = (
  id: string,
  presetInitialValues: InfoHubFormValues | undefined,
  initialFormValues: InfoHubFormValues
): InfoHubFormValues => (!id && presetInitialValues ? presetInitialValues : initialFormValues);

// A new message renders immediately and lets the reference lists stream into
// their selects; only editing/copying blocks on the fetches needed to
// reconstruct the subject's initial values, and a save-in-progress always blocks.
export const shouldBlockForm = (id: string, isFetching: boolean, isBusy: boolean): boolean =>
  isBusy || (Boolean(id) && isFetching);

interface StateProps {
  id: string;
  contentTypes: string[];
  topics: Topic[];
  aggregators: Aggregator[];
  units: SelectOption[];
  initialFormValues: InfoHubFormValues;
  isFetching: boolean;
  loading: InfoHubFieldLoading;
  errorMessage?: string;
}

interface DeleteAggregatorParams {
  subject: OptionValue;
  aggregators: OptionValue[];
}

interface DispatchProps {
  listInfoHubTopics: () => void;
  listInfoHubTopicsReset: () => void;
  listAggregators: () => void;
  listAggregatorsReset: () => void;
  addInfoHubSubjectDetail: (data: InfoHubSubjectPayload) => Promise<SaveSubjectResult>;
  updateInfoHubSubjectDetail: (
    id: string,
    data: InfoHubSubjectPayload
  ) => Promise<SaveSubjectResult>;
  infoHubSubjectDetail: (id: string) => void;
  infoHubSubjectDetailReset: () => void;
  listInfoHubSubjectAggregators: (id: string) => void;
  listInfoHubSubjectAggregatorsReset: () => void;
  infoHubDeleteAggregatorFromSubject: (params: DeleteAggregatorParams) => unknown;
  listOrgTypes: () => void;
  listUnitsOfMeasure: () => void;
  listUnitsOfMeasureReset: () => void;
}

type InfoHubFormWrapperProps = StateProps & DispatchProps & RouteComponentProps;

// This component is used to display the detail
// of a Aggregator Organization
export const InfoHubFormWrapper = ({
  isFetching,
  errorMessage,
  history,
  id,
  initialFormValues,
  contentTypes,
  topics,
  aggregators,
  units,
  loading,
  listInfoHubTopics,
  listInfoHubTopicsReset,
  listAggregators,
  listAggregatorsReset,
  addInfoHubSubjectDetail,
  updateInfoHubSubjectDetail,
  infoHubSubjectDetail,
  infoHubSubjectDetailReset,
  listInfoHubSubjectAggregators,
  infoHubDeleteAggregatorFromSubject,
  listOrgTypes,
  listUnitsOfMeasure,
  listUnitsOfMeasureReset,
}: InfoHubFormWrapperProps) => {
  const locationState = history.location?.state as
    | { isCopy?: boolean; contentType?: string }
    | undefined;
  const isCopy = Boolean(locationState?.isCopy);
  const presetContentType = locationState?.contentType;

  const { t } = useTranslation();
  const [isBusy, setBusy] = useState(false);

  // Memoized so the preset reference stays stable across the re-renders that
  // happen as the reference lists stream in (otherwise the form would reset the
  // user's input on each one).
  const presetInitialValues = useMemo(
    () => buildPresetInitialValues(presetContentType),
    [presetContentType]
  );
  const formInitialValues = selectFormInitialValues(id, presetInitialValues, initialFormValues);
  const isLoading = shouldBlockForm(id, isFetching, isBusy);

  // On component mount the data are fetched or read
  // on component unmount data are reset
  useEffect(() => {
    if (id) {
      infoHubSubjectDetail(id);
      if (!isCopy) {
        listInfoHubSubjectAggregators(id);
      }
    }
    listInfoHubTopics();
    listAggregators();
    listOrgTypes();
    listUnitsOfMeasure();
    return () => {
      listInfoHubTopicsReset();
      listAggregatorsReset();
      infoHubSubjectDetailReset();
      listUnitsOfMeasureReset();
    };
  }, []);

  // On click validate a requests are made
  // to save Aggregator, after saving the
  // Aggregators list is displayed
  const onClickValidateAndClose = (formValues: InfoHubFormValues) => {
    saveSubject(formValues);
  };

  // This function is used to save a InfoHub Subject
  const saveSubject = (formValues: InfoHubFormValues) => {
    setBusy(true);
    const data = adaptFromValues(formValues);
    const promise =
      id && !isCopy ? updateInfoHubSubjectDetail(id, data) : addInfoHubSubjectDetail(data);

    promise
      .then(result => {
        if (!result) return result;
        const currentAggregators = initialFormValues.aggregators
          ? initialFormValues.aggregators.map(aggregator => aggregator.value)
          : [];

        const deletedAggregators = currentAggregators.filter(
          aggregator => !data.aggregators.includes(aggregator)
        );
        if (deletedAggregators.length > 0) {
          const params: DeleteAggregatorParams = {
            subject: id && !isCopy ? id : (result.data?.id ?? ""),
            aggregators: deletedAggregators,
          };
          infoHubDeleteAggregatorFromSubject(params);
        }
        return Promise.all([result]);
      })
      .finally(() => {
        setBusy(false);
      })
      .then(result => {
        if (result) {
          history.push("/wfpManager/infohub");
        }
      });
  };

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-col gap-4">
        <PageBreadcrumb
          items={[
            { label: t("infoHub.title"), href: "/wfpManager/infohub" },
            { label: t("infoHub.form.pageTitle") },
          ]}
        />
        <div className="flex items-center gap-x-2">
          <span className="inline-flex size-10 shrink-0 items-center justify-center rounded-lg bg-blue-100 p-2 text-blue-600">
            <Mails />
          </span>
          <h1 className="font-semibold text-2xl">{t("infoHub.form.pageTitle")}</h1>
        </div>
      </div>
      <Form
        initialValues={formInitialValues}
        isFetching={isLoading}
        errorMessage={errorMessage}
        onClickValidateAndClose={onClickValidateAndClose}
        contentTypes={contentTypes}
        topics={topics}
        aggregators={aggregators}
        units={units}
        loading={loading}
        isCopy={isCopy}
      />
    </main>
  );
};

interface RequestState<T> {
  isFetching: boolean;
  error?: Record<string, unknown>;
  data: T;
}

interface RootState {
  infoHubSubjectDetail: RequestState<InfoHubSubjectData>;
  listInfoHubTopics: RequestState<{ results?: Topic[] }>;
  listAggregators: RequestState<{ results?: Aggregator[] }>;
  listInfoHubSubjectAggregators: RequestState<OptionValue[]>;
  listOrgTypes: RequestState<{ results?: OrgType[] }>;
  listUnitsOfMeasure: RequestState<{ results?: RawUnitOfMeasurement[] }>;
}

interface RouteProps {
  match: { params: { id?: string } };
}

// Starting from the redux state it gets data related to logged in user
export const mapStateToProps = (state: RootState, props: RouteProps): StateProps => {
  const selectedAggregators = state.listInfoHubSubjectAggregators.data || [];
  const allAggregators = state.listAggregators.data.results || [];
  const units: SelectOption[] = adaptUnitsOfMeasure(state.listUnitsOfMeasure.data.results);
  return {
    id: props.match.params.id || "",
    // MCF content types are only available if the selected country has the MCF org type configured
    contentTypes: (() => {
      const types = [
        "Aggregator Information",
        "Farmer Training",
        "Input Distribution",
        "Equipment Distribution",
      ];
      const orgTypes = state.listOrgTypes.data.results || [];
      const hasMcfOrgType = orgTypes.some(
        orgType => orgType.name === MCF_COOPERATING_PARTNER_ORG_TYPE
      );
      if (hasMcfOrgType) {
        types.push("MCF CP Information", "MCF Participants Activities");
      }
      return types;
    })(),
    topics: state.listInfoHubTopics.data.results || [],
    aggregators: allAggregators || [],
    units,
    initialFormValues: adaptInitialFormValues(
      state.infoHubSubjectDetail.data,
      selectedAggregators,
      allAggregators,
      units
    ),
    isFetching:
      state.infoHubSubjectDetail.isFetching ||
      state.listInfoHubTopics.isFetching ||
      state.listAggregators.isFetching ||
      state.listInfoHubSubjectAggregators.isFetching ||
      state.listOrgTypes.isFetching ||
      state.listUnitsOfMeasure.isFetching,
    // Per-field flags so a new message can render immediately and show a loading
    // affordance on the selects whose options are still streaming in.
    loading: {
      topics: state.listInfoHubTopics.isFetching,
      aggregators: state.listAggregators.isFetching,
      units: state.listUnitsOfMeasure.isFetching,
    },
    errorMessage:
      getResponseErrorMessage(state.infoHubSubjectDetail.error) ||
      getResponseErrorMessage(state.listInfoHubTopics.error) ||
      getResponseErrorMessage(state.listAggregators.error) ||
      getResponseErrorMessage(state.listInfoHubSubjectAggregators.error),
  };
};

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = <TResult,>(dispatch: AppDispatch, action: unknown): TResult =>
  dispatch(action as UnknownAction) as unknown as TResult;

// Maps functions to dispatch actions
export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    listInfoHubTopics: () => dispatchAction(dispatch, listInfoHubTopics()),
    listInfoHubTopicsReset: () => dispatchAction(dispatch, listInfoHubTopicsReset()),
    listAggregators: () => dispatchAction(dispatch, listAggregators({ page_size: 9999 })),
    listAggregatorsReset: () => dispatchAction(dispatch, listAggregatorsReset()),
    addInfoHubSubjectDetail: data =>
      dispatchAction<Promise<SaveSubjectResult>>(dispatch, addInfoHubSubjectDetail(data)),
    updateInfoHubSubjectDetail: (id, data) =>
      dispatchAction<Promise<SaveSubjectResult>>(dispatch, updateInfoHubSubjectDetail(id, data)),
    infoHubSubjectDetail: id => dispatchAction(dispatch, infoHubSubjectDetail(id)),
    infoHubSubjectDetailReset: () => dispatchAction(dispatch, infoHubSubjectDetailReset()),
    listInfoHubSubjectAggregators: id =>
      dispatchAction(dispatch, listInfoHubSubjectAggregators({ subject: id }, "aggregators/")),
    listInfoHubSubjectAggregatorsReset: () =>
      dispatchAction(dispatch, listInfoHubSubjectAggregatorsReset()),
    infoHubDeleteAggregatorFromSubject: params =>
      dispatchAction(dispatch, infoHubDeleteAggregatorFromSubject(undefined, undefined, params)),
    listOrgTypes: () => dispatchAction(dispatch, listOrgTypes()),
    listUnitsOfMeasure: () => dispatchAction(dispatch, listUnitsOfMeasure()),
    listUnitsOfMeasureReset: () => dispatchAction(dispatch, listUnitsOfMeasureReset()),
  };
};

// The component uses the redux store
export const InfoHubForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(InfoHubFormWrapper));
